/**
 * <BraidedRevenueWidget> — sidebar card for a member showing open
 * braided-revenue suggestions (missed co-billing opportunities).
 *
 * Backend: GET /api/billing-ai/braided-revenue?member_id={id}&status=open
 *
 * Optional: triggers a fresh scan on mount via /braided-revenue/scan so the
 * widget surfaces opportunities even if no batch sweep has run.
 */

import { useEffect, useState } from 'react';
import { Workflow, CheckCircle, X, Loader2 } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface BraidedSuggestion {
  id: number;
  member_id: number;
  program_id: number;
  suggested_code: string;
  rationale: string;
  estimated_revenue: number;
  status: string;
}

interface Props {
  memberId: number;
  /** Scan on mount before listing (default false to keep widget cheap) */
  scanOnMount?: boolean;
  /** Max rows to show (default 3) */
  maxRows?: number;
  onNavigateToBillingAI?: () => void;
}

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function BraidedRevenueWidget({
  memberId,
  scanOnMount = false,
  maxRows = 3,
  onNavigateToBillingAI,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState<BraidedSuggestion[]>([]);

  async function load() {
    setBusy(true);
    try {
      if (scanOnMount) {
        await fetch(
          getWeaveinApiUrl(`/api/billing-ai/braided-revenue/scan?member_id=${memberId}`),
          { method: 'POST', credentials: 'include', headers: authHeaders() }
        );
      }
      const r = await fetch(
        getWeaveinApiUrl(`/api/billing-ai/braided-revenue?member_id=${memberId}&status=open`),
        { credentials: 'include', headers: authHeaders() }
      );
      if (r.ok) setSuggestions(await r.json());
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  async function action(id: number, kind: 'accept' | 'dismiss') {
    await fetch(getWeaveinApiUrl(`/api/billing-ai/braided-revenue/${id}/action`), {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ action: kind, reason: null }),
    });
    void load();
  }

  if (!busy && suggestions.length === 0) return null;

  const totalRevenue = suggestions.reduce((sum, s) => sum + (Number(s.estimated_revenue) || 0), 0);

  return (
    <div className="rounded-lg border border-v2border bg-surface p-3">
      <div className="mb-2 flex items-center gap-1.5">
        <Workflow className="h-3.5 w-3.5 text-v2accent" />
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          Revenue opportunities
        </span>
        {totalRevenue > 0 && (
          <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
            ${totalRevenue.toFixed(0)}
          </span>
        )}
      </div>

      {busy && suggestions.length === 0 && (
        <p className="text-xs text-ink-400">
          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
          Scanning…
        </p>
      )}

      <ul className="space-y-2">
        {suggestions.slice(0, maxRows).map((s) => (
          <li
            key={s.id}
            className="rounded-md border border-v2border-muted bg-paper-2 px-2.5 py-2 text-xs"
          >
            <div className="mb-1 flex items-center gap-1.5">
              <code className="rounded bg-paper px-1.5 py-0.5 text-[11px] font-medium text-ink">
                {s.suggested_code}
              </code>
              <span className="ml-auto text-[10px] font-medium text-emerald-700">
                +${Number(s.estimated_revenue || 0).toFixed(0)}
              </span>
            </div>
            <p className="mb-1.5 leading-snug text-ink-500">{s.rationale}</p>
            <div className="flex gap-1.5">
              <button
                onClick={() => action(s.id, 'accept')}
                className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100"
                title="Accept"
              >
                <CheckCircle className="h-2.5 w-2.5" />
                Accept
              </button>
              <button
                onClick={() => action(s.id, 'dismiss')}
                className="inline-flex items-center gap-1 rounded bg-paper px-1.5 py-0.5 text-[10px] text-ink-500 hover:bg-paper-2"
              >
                <X className="h-2.5 w-2.5" />
                Dismiss
              </button>
            </div>
          </li>
        ))}
      </ul>

      {suggestions.length > maxRows && onNavigateToBillingAI && (
        <button
          type="button"
          onClick={onNavigateToBillingAI}
          className="mt-2 w-full text-center text-[11px] text-v2accent hover:underline"
        >
          See all {suggestions.length} →
        </button>
      )}
    </div>
  );
}
